import React, { useState, useEffect } from "react";
import Page from "src/components/page";
import { styled } from "@mui/material/styles";
import {
  Step,
  Stack,
  Stepper,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  Box,
  Alert,
  Card,
  Typography,
} from "@mui/material";
import { bgGradient } from "src/utils/cssStyles";
import Iconify from "src/components/iconify";
import { useLocales } from "src/locales";
import Logo from "src/components/logo";
import BusinessDetailsSimplified from "src/sections/@dashboard/userKyc/BusinessDetailsSimplified";
import DocumentsUploadSimplified from "src/sections/@dashboard/userKyc/DocumentsUploadSimplified";
import PreviewSimplified from "src/sections/@dashboard/userKyc/PreviewSimplified";
import { getUniqueId } from "src/utils/uniqueId";
import { kycApi } from "src/services/kycApi";

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      ...bgGradient({
        startColor: theme.palette.primary.light,
        endColor: theme.palette.primary.main,
      }),
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      ...bgGradient({
        startColor: theme.palette.primary.light,
        endColor: theme.palette.primary.main,
      }),
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    borderRadius: 1,
    backgroundColor: theme.palette.divider,
  },
}));

const imageStyle = {
  maxWidth: "400px",
  minWidth: "300px",
};

const STEPS = [
  "businessDetails",
  "documents",
  "preview",
];

const ColorlibStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  zIndex: 1,
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.disabled,
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[300]
      : theme.palette.grey[700],
  ...(ownerState.active && {
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
    color: theme.palette.common.white,
    ...bgGradient({
      startColor: theme.palette.primary.light,
      endColor: theme.palette.primary.main,
    }),
  }),
  ...(ownerState.completed && {
    color: theme.palette.common.white,
    ...bgGradient({
      startColor: theme.palette.primary.light,
      endColor: theme.palette.primary.main,
    }),
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon } = props;

  const icons = {
    1: <Iconify icon="bxs:business" width={24} />,
    2: <Iconify icon="ion:documents" width={24} />,
    3: <Iconify icon="fluent:preview-link-16-filled" width={24} />,
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

const KycForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uniqueId, setUniqueId] = useState(null);
  const [checkingUniqueId, setCheckingUniqueId] = useState(true);
  const [uniqueIdStatus, setUniqueIdStatus] = useState(null); // 'new', 'in_progress', 'pending_review', 'rejected', 'approved', 'error'
  const [uniqueIdError, setUniqueIdError] = useState(null);

  const { t } = useLocales();

  // Check for unique_id in URL and validate it via API
  useEffect(() => {
    const checkUniqueId = async () => {
      const uniqueIdFromUrl = getUniqueId();
      
      if (!uniqueIdFromUrl) {
        setCheckingUniqueId(false);
        return;
      }

      setUniqueId(uniqueIdFromUrl);
      setCheckingUniqueId(true);

      try {
        const response = await kycApi.checkUniqueKey(uniqueIdFromUrl);
        const statusCode = response?.status || response?.response?.status;

        if (statusCode === 201) {
          // Unique key created (new)
          setUniqueIdStatus('new');
          console.log("✅ New unique key - user can proceed");
        } else if (statusCode === 200) {
          // Key exists with status
          const status = response?.data?.data?.status || response?.data?.status || 'in_progress';
          setUniqueIdStatus(status);
          console.log("✅ Unique key exists with status:", status);
        } else {
          // Unexpected response
          setUniqueIdStatus('error');
          setUniqueIdError("Unexpected response from server");
        }
      } catch (error) {
        const statusCode = error?.response?.status;
        
        if (statusCode === 400) {
          // KYC already approved
          setUniqueIdStatus('approved');
          setUniqueIdError(error?.response?.data?.message || "KYC has already been approved");
        } else if (statusCode === 403) {
          // KYC already submitted
          setUniqueIdStatus('submitted');
          setUniqueIdError(error?.response?.data?.message || "Your KYC is Already Submitted");
        } else {
          // Other errors
          setUniqueIdStatus('error');
          setUniqueIdError(error?.response?.data?.message || error?.message || "Failed to validate unique key");
        }
      } finally {
        setCheckingUniqueId(false);
      }
    };

    checkUniqueId();
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const onBackClick = () => {};

  function getStepContent(step) {
    switch (step) {
      case 0:
        return (
          <BusinessDetailsSimplified
            handleReset={handleReset}
            handleNext={handleNext}
            handleBack={handleBack}
            uniqueId={uniqueId}
            uniqueIdStatus={uniqueIdStatus}
          />
        );
      case 1:
        return (
          <DocumentsUploadSimplified
            handleNext={handleNext}
            handleBack={handleBack}
            uniqueId={uniqueId}
          />
        );
      case 2:
        return (
          <PreviewSimplified 
            handleBack={handleBack} 
            onCompleteFlow={onBackClick}
            uniqueId={uniqueId}
          />
        );
      default:
        return "Unknown step";
    }
  }

  // Show loading while checking unique_id
  if (checkingUniqueId) {
    return (
      <Page title={"KYB/KYC Form"}>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 3,
            }}
          >
            <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
          </Box>
          <Card sx={{ p: 4, mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <Typography>Validating unique key...</Typography>
            </Box>
          </Card>
        </Stack>
      </Page>
    );
  }

  // Show error if unique_id is missing
  if (!uniqueId) {
    return (
      <Page title={"KYB/KYC Form"}>
        <Stack spacing={4}>
          {/* Logo at the top, centered */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 3,
            }}
          >
            <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
          </Box>

          {/* Error Message */}
          <Card sx={{ p: 4, mt: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Missing Required Parameter
              </Typography>
              <Typography variant="body2">
                The <strong>unique_id</strong> parameter is required in the URL to proceed with the KYC form.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Please ensure your URL includes the unique_id parameter, for example:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', color: 'primary.main' }}>
                {window.location.origin}{window.location.pathname}?unique_id=XXXXXX
              </Typography>
            </Alert>
          </Card>
        </Stack>
      </Page>
    );
  }

  // Show error if unique_id validation failed
  if (uniqueIdStatus === 'error') {
    return (
      <Page title={"KYB/KYC Form"}>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 3,
            }}
          >
            <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
          </Box>
          <Card sx={{ p: 4, mt: 4 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Validation Error
              </Typography>
              <Typography variant="body2">
                {uniqueIdError || "Failed to validate unique key. Please check your URL and try again."}
              </Typography>
            </Alert>
          </Card>
        </Stack>
      </Page>
    );
  }

  // Show message if KYC is already approved
  if (uniqueIdStatus === 'approved') {
    return (
      <Page title={"KYB/KYC Form"}>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 3,
            }}
          >
            <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
          </Box>
          <Card sx={{ p: 4, mt: 4 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                KYC Already Approved
              </Typography>
              <Typography variant="body2">
                {uniqueIdError || "This KYC application has already been approved. No further action is required."}
              </Typography>
            </Alert>
          </Card>
        </Stack>
      </Page>
    );
  }

  // Show message if KYC is already submitted
  if (uniqueIdStatus === 'submitted') {
    return (
      <Page title={"KYB/KYC Form"}>
        <Stack spacing={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 3,
            }}
          >
            <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
          </Box>
          <Card sx={{ p: 4, mt: 4 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                KYC Already Submitted
              </Typography>
              <Typography variant="body2">
                {uniqueIdError || "Your KYC is Already Submitted. Please wait for admin review."}
              </Typography>
            </Alert>
          </Card>
        </Stack>
      </Page>
    );
  }

  return (
    <Page title={"KYB/KYC Form"}>
      <Stack spacing={4}>
        {/* Logo at the top, centered */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            py: 3,
          }}
        >
          <Logo disabledLink sx={{ width: 220, height: 'auto' }} />
        </Box>

        {/* KYB Form Stepper */}
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
        >
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel StepIconComponent={ColorlibStepIcon}>
                {t(label)}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Form Content */}
        <Stack>{getStepContent(activeStep)}</Stack>
      </Stack>
    </Page>
  );
};

export default KycForm;
